import clsx from 'clsx';
import type * as React from 'react';
import { Text, View } from 'react-native';

import { type IconDefinition, renderIcon } from '@gitbook/spine-react';

import { flex, spacing } from '../constants';
import { Interactive, type InteractiveState } from '../Interactive';
import { OneLineText } from '../OneLineText';
import { themedFonts, useCurrentTheme } from '../theme';
import { BaseCard, type BaseCardProps } from './BaseCard';

const InteractiveOneLineText = Interactive.createInteractiveComponent(OneLineText);

export interface MediaCardProps extends BaseCardProps {
    icon?: IconDefinition;

    /**
     * Main text for the card.
     */
    title: string;

    /**
     * Small text displayed next to the title.
     */
    subtitle?: string;

    /**
     * Text displayed below the title.
     */
    details?: string;

    /**
     * If the card should link somewhere, use this prop to give that card a link for accessibility
     * and styling purposes.
     */
    href?: string;

    /**
     * Action buttons displayed on the right side of the card.
     */
    buttons?: React.ReactNode[];
}

/**
 * Card to represent an external content with an optional square icon. The card will display the icon and its content
 * horizontally, allowing for things like media links or embed cards.
 */
export function MediaCard(props: MediaCardProps) {
    const { icon, title, subtitle, details, style, buttons = [], ...rest } = props;
    const [, font] = useCurrentTheme(themedFonts);
    const interactive = Interactive.useInteractiveState();
    return (
        <BaseCard
            style={[flex.alignItemsCenter, spacing.Padding.all400, style]}
            {...rest}
            {...interactive.events}
        >
            {icon ? <MediaCardIcon icon={icon} interactive={interactive} /> : null}
            <View style={[flex.flex1]}>
                <View style={[flex.row, flex.alignItemsCenter]}>
                    <InteractiveOneLineText.Child
                        style={[font.Body, font.Medium, font.text]}
                        activeStyle={font.primary}
                        descendantOf={BaseCard.interactiveHandle}
                    >
                        {title}
                    </InteractiveOneLineText.Child>
                    {subtitle ? (
                        <OneLineText style={[font.Label, font.textMuted, spacing.Margin.left400]}>
                            {subtitle}
                        </OneLineText>
                    ) : null}
                </View>
                {details ? (
                    <View>
                        <Text style={[font.Label, font.textMid]}>{details}</Text>
                    </View>
                ) : null}
            </View>
            {buttons.length > 0 ? (
                <View style={[flex.row, flex.alignItemsCenter, spacing.Margin.left400]}>
                    {buttons.map((button, index) =>
                        button ? (
                            <View style={[spacing.Margin.left200]} key={index}>
                                {button}
                            </View>
                        ) : null
                    )}
                </View>
            ) : null}
        </BaseCard>
    );
}

function MediaCardIcon(props: { icon: IconDefinition; interactive: InteractiveState }) {
    const { icon, interactive } = props;
    return (
        <div className={clsx('mr-4 flex', interactive.hovered ? 'text-primary' : 'text-muted')}>
            {renderIcon(icon, { size: 32 })}
        </div>
    );
}
